import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Descriptions,
  Typography,
  Modal,
  Input,
  Select,
  Space,
  Popconfirm,
  message,
  Divider,
} from "antd";
import { useNavigate } from "react-router-dom";
import { PlusOutlined, LinkOutlined, DisconnectOutlined, EyeOutlined } from "@ant-design/icons";
import {
  createMasterContainer,
  addContainerToMaster,
  removeContainerFromMaster,
  fetchMasterContainersForFilter,
} from "../../services/masterContainerService";
import type { MasterContainer, Container } from "../../types/booking";
import { getMasterTransportTypeName } from "../../utils/masterContainerTypeUtils";

interface BillOfLoadingSectionProps {
  /** Container from booking.container_info (may include master_container_info) */
  container: Container;
  onRefresh: () => void;
}

function getMastersFromContainer(container: Container): { id: number; reference: string | null }[] {
  const mcc = (container as { master_container_info?: unknown }).master_container_info;
  const mccList = Array.isArray(mcc) ? mcc : mcc ? [mcc] : [];
  const masters: { id: number; reference: string | null }[] = [];
  for (const m of mccList as { master_containers?: { id: number; reference: string | null } | { id: number; reference: string | null }[] }[]) {
    const mc = m?.master_containers;
    const single = Array.isArray(mc) ? mc[0] : mc;
    if (single?.id) masters.push({ id: single.id, reference: single.reference ?? null });
  }
  return masters;
}

export const BillOfLoadingSection: React.FC<BillOfLoadingSectionProps> = ({
  container,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [newReference, setNewReference] = useState("");
  const [selectedMasterId, setSelectedMasterId] = useState<string | null>(null);
  const [masters, setMasters] = useState<MasterContainer[]>([]);
  const [loadingMasters, setLoadingMasters] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const mastersFromContainer = getMastersFromContainer(container);
  const hasMaster = mastersFromContainer.length > 0;

  useEffect(() => {
    if (!linkModalOpen) return;
    setLoadingMasters(true);
    fetchMasterContainersForFilter(
      container.transport_type != null
        ? { transportTypeId: container.transport_type }
        : undefined
    )
      .then((r) => {
        if (!r.error && r.data) setMasters(r.data);
      })
      .finally(() => setLoadingMasters(false));
  }, [linkModalOpen, container.transport_type]);

  const handleAddNew = async () => {
    const ref = newReference.trim();
    if (!ref) {
      message.warning("Enter a reference for the new bill of lading (B/L).");
      return;
    }
    if (container.transport_type == null) {
      message.warning(
        "Container has no transport type. Set transport type first."
      );
      return;
    }
    setSubmitting(true);
    try {
      const createRes = await createMasterContainer({
        reference: ref,
        transport_type: container.transport_type,
      });
      if (createRes.error || !createRes.data?.id) {
        message.error(createRes.error?.message || "Failed to create bill of lading (B/L).");
        return;
      }
      const addRes = await addContainerToMaster(createRes.data.id, container.id);
      if (!addRes.success) {
        message.error(
          (addRes.error as Error)?.message ||
            "Failed to link container to bill of lading (B/L)."
        );
        return;
      }
      message.success("Bill of lading (B/L) created and linked.");
      setAddModalOpen(false);
      setNewReference("");
      onRefresh();
    } finally {
      setSubmitting(false);
    }
  };

  const handleLinkExisting = async () => {
    const masterId = selectedMasterId ? parseInt(selectedMasterId, 10) : NaN;
    if (!Number.isFinite(masterId)) {
      message.warning("Select a bill of lading (B/L) to link.");
      return;
    }
    setSubmitting(true);
    try {
      for (const mc of mastersFromContainer) {
        const rem = await removeContainerFromMaster(mc.id, container.id);
        if (!rem.success) {
          message.error(rem.error?.message || "Failed to unlink from current bill of lading (B/L).");
          return;
        }
      }
      const res = await addContainerToMaster(masterId, container.id);
      if (!res.success) {
        message.error(res.error?.message || "Failed to link to bill of lading (B/L).");
        return;
      }
      message.success("Container linked to bill of lading (B/L).");
      setLinkModalOpen(false);
      setSelectedMasterId(null);
      onRefresh();
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlink = async (masterId: number) => {
    setSubmitting(true);
    try {
      const res = await removeContainerFromMaster(masterId, container.id);
      if (!res.success) {
        message.error(res.error?.message || "Failed to unlink.");
        return;
      }
      message.success("Unlinked from bill of lading (B/L).");
      onRefresh();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      title="Bill of lading (B/L)"
      style={{ marginBottom: 16 }}
      extra={
        <Space>
          {!hasMaster && (
            <>
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setAddModalOpen(true)}
              >
                Add new
              </Button>
              <Button
                size="small"
                icon={<LinkOutlined />}
                onClick={() => setLinkModalOpen(true)}
              >
                Link to existing
              </Button>
            </>
          )}
        </Space>
      }
    >
      {hasMaster ? (
        <>
          <Descriptions bordered column={1}>
            {mastersFromContainer.map((mc, idx) => (
              <React.Fragment key={mc.id}>
                <Descriptions.Item label="Reference">
                  <Space>
                    <span>{mc.reference || "—"}</span>
                    <Button
                      type="link"
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() =>
                        navigate(
                          `/admin/containers?tab=master-containers&master_id=${mc.id}`
                        )
                      }
                    >
                      View
                    </Button>
                    <Popconfirm
                      title="Unlink from this bill of lading (B/L)?"
                      onConfirm={() => handleUnlink(mc.id)}
                      okText="Unlink"
                      cancelText="Cancel"
                    >
                      <Button
                        type="link"
                        danger
                        size="small"
                        icon={<DisconnectOutlined />}
                        disabled={submitting}
                      >
                        Unlink
                      </Button>
                    </Popconfirm>
                  </Space>
                </Descriptions.Item>
                {mastersFromContainer.length > 1 && idx < mastersFromContainer.length - 1 && (
                  <Descriptions.Item>
                    <Divider style={{ margin: "8px 0" }} />
                  </Descriptions.Item>
                )}
              </React.Fragment>
            ))}
          </Descriptions>
          <div style={{ marginTop: 8 }}>
            <Button
              size="small"
              icon={<LinkOutlined />}
              onClick={() => {
                setSelectedMasterId(null);
                setLinkModalOpen(true);
              }}
              disabled={submitting}
            >
              Link to different bill of lading (B/L)
            </Button>
          </div>
        </>
      ) : (
        <Typography.Text type="secondary">
          No bill of lading (B/L) linked
        </Typography.Text>
      )}

      <Modal
        title="Add new bill of lading (B/L)"
        open={addModalOpen}
        onCancel={() => !submitting && setAddModalOpen(false)}
        onOk={handleAddNew}
        okText="Create and link"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Text type="secondary">
            Create a new bill of lading (B/L) and link this container to it
            {container.transport_type != null
              ? " (same transport type as this container)."
              : "."}
          </Typography.Text>
          <Input
            placeholder="Reference (e.g. BOL number)"
            value={newReference}
            onChange={(e) => setNewReference(e.target.value)}
            onPressEnter={handleAddNew}
          />
        </Space>
      </Modal>

      <Modal
        title="Link to existing bill of lading (B/L)"
        open={linkModalOpen}
        onCancel={() => !submitting && setLinkModalOpen(false)}
        onOk={handleLinkExisting}
        okText="Link"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Text type="secondary">
            Select a bill of lading (B/L) to link this container to
            {container.transport_type != null
              ? " (only matching transport type)."
              : "."}
          </Typography.Text>
          <Select
            style={{ width: "100%" }}
            placeholder="Select bill of lading (B/L)"
            loading={loadingMasters}
            value={selectedMasterId}
            onChange={setSelectedMasterId}
            showSearch
            optionFilterProp="label"
            options={masters.map((m) => ({
              value: m.id.toString(),
              label: `${m.reference || `#${m.id}`}${
                m.transport_type != null
                  ? ` (${getMasterTransportTypeName(m)})`
                  : ""
              }`,
            }))}
          />
        </Space>
      </Modal>
    </Card>
  );
};

export default BillOfLoadingSection;
